import React, { Component } from 'react';
import { get, set } from 'lodash';
import { Button, Modal } from 'antd';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import './EditableList.css';
import PropTypes from 'prop-types';
import { pushLog2DV2LogServer } from '../../../../services/logging';

class EditableList extends Component {
  state = {
    adding: false,
    editing: false,
    disabledOkButton: false,
    loading: false,
    dataSource: [],
    deleteList: [],
    originalData: null,
    activeItem: null
  };

  acceptDataSource = () => {
    this.setState({
      deleteList: [],
      dataSource: (this.props.dataSource || [])
        .map(this.initSavedItem)
        .map(this.initItem)
    });
  };

  componentDidMount () {
    this.acceptDataSource();
  }

  componentDidUpdate (prevProps) {
    if (prevProps.dataSource !== this.props.dataSource) {
      this.acceptDataSource();
    }
  }

  initSavedItem = (item) => {
    item.$changed = false;
    item.$savedItem = { ...item };
    return item;
  };

  handleChange = () => {
    const { deleteList, dataSource, adding, editing } = this.state;
    if (adding || editing) return;
    const { onChange } = this.props;
    const changes = {
      deletedItems: [...deleteList].map(this.cleanUpItem),
      addedItems: dataSource
        .filter((item) => !item.$savedItem)
        .map(this.cleanUpItem),
      updatedItems: dataSource
        .filter((item) => item.$savedItem && item.$changed)
        .map(this.checkChangesItem)
        .map(this.cleanUpItem)
        .filter((item) => item._changes_)
    };
    if (
      onChange &&
      (changes.deletedItems.length ||
        changes.addedItems.length ||
        changes.updatedItems.length)
    ) { onChange(changes); }
    return changes;
  };

  checkChangesItem = (item) => {
    const changes = {};
    let changed = false;
    Object.entries(item.$savedItem).forEach(([key, value]) => {
      // Comparision may fail if change to !==
      // eslint-disable-next-line
      if (value != item[key] && key !== '$changed') {
        changes[key] = item[key];
        changed = true;
      }
    });
    if (changed) {
      item._changes_ = changes;
    } else {
      delete item._changes_;
    }
    return item;
  };

  cleanUpItem = (item) => {
    const cloneItem = { ...item };
    delete cloneItem.set;
    delete cloneItem.bind;
    delete cloneItem.edit;
    delete cloneItem.delete;
    delete cloneItem.$changed;
    delete cloneItem.$savedItem;
    delete cloneItem.$isNewItem;
    delete cloneItem.$isValid;
    delete cloneItem.$validation;
    return cloneItem;
  };

  initItem = (item, index) => {
    delete item.$isNewItem;
    item.set = (obj = {}) => {
      let changed = false;
      Object.entries(obj).forEach(([key, value]) => {
        item[key] = value;
        changed = true;
      });
      item.$changed = item.$changed || changed;
      this.setState({ dataSource: [...this.state.dataSource] });
      this.acceptChanges(
        [...this.state.dataSource],
        () => changed && this.handleChange()
      );
    };
    item.bind = (name, options = {}) => {
      const {
        bindProp = 'value',
        defaultValue = null,
        onChange,
        onReadValue
      } = options;
      const value = get(item, name, defaultValue);
      return {
        [bindProp]: onReadValue ? onReadValue(value) : value,
        onChange: (e) => {
          let value = e;
          if (e && typeof e === 'object' && 'target' in e) {
            value = e.target[bindProp];
          }
          set(item, name, value);
          item.$changed = true;
          item.set();
          if (onChange) onChange(value);
        },
        ...(item.$validation ? item.$validation[name] : null)
      };
    };
    item.edit = () => {
      item.set({
        $isValid: true,
        $validation: {}
      });
      this.setState({
        [item.$savedItem ? 'editing' : 'adding']: true,
        originalData: { ...item },
        activeItem: item
      });
    };
    item.delete = () => {
      Modal.confirm({
        title: 'Do you Want to delete this record?',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          const { dataSource, deleteList } = this.state;
          dataSource.splice(index, 1);
          this.acceptChanges(dataSource);
          if (item.$savedItem) {
            this.setState({ deleteList: [...deleteList, item] }, () =>
              this.handleChange()
            );
          } else {
            this.handleChange();
          }
        }
      });
    };
    item.error = (name, message) => {
      item.set({
        $isValid: false,
        $validation: {
          ...item.$validation,
          [name]: {
            hasFeedback: true,
            validateStatus: 'error',
            help: message
          }
        }
      });
    };
    return item;
  };

  renderItem = (item, index) => {
    const { renderItem, isReadOnly = false } = this.props;
    if (!renderItem) return null;
    const itemContent = renderItem(item, index);
    return (
      <div className="turing-ui-v2-editable-list-item" key={index}>
        <div className="turing-ui-v2-editable-list-item-content">
          {itemContent}
        </div>
        {isReadOnly
          ? null
          : (
            <div className="turing-ui-v2-editable-list-item-actions">
              <IconButton onClick={item.edit}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={item.delete}>
                <DeleteIcon />
              </IconButton>
            </div>
          )}
      </div>
    );
  };

  renderForm = () => {
    const { renderForm } = this.props;
    const { activeItem } = this.state;
    if (!renderForm || !activeItem) return null;
    return renderForm(activeItem);
  };

  handleAddClick = () => {
    const { addEvent } = this.props;
    if (addEvent) {
      // [LOGGING] addEvent
      pushLog2DV2LogServer.info('SEGMENTIO', addEvent, {}, {}, null);
    }
    const newItem = this.initItem({});
    newItem.$isNewItem = true;
    newItem.edit();
  };

  acceptChanges = (dataSource = [], callback) => {
    this.setState({ dataSource: [...dataSource.map(this.initItem)] }, callback);
  };

  handleOkClick = () => {
    const { activeItem, dataSource, adding } = this.state;
    const { validation, saveEvent } = this.props;
    if (validation) {
      activeItem.$isValid = true;
      activeItem.$validation = {};
      validation(activeItem);
      if (!activeItem.$isValid) {
        activeItem.set();
        return;
      }
    }
    if (activeItem.$isNewItem) {
      dataSource.push(activeItem);
    }
    if (adding && saveEvent) {
      // [LOGGING] saveEvent
      pushLog2DV2LogServer.info('SEGMENTIO', saveEvent, {}, {}, null);
    }
    this.setState({ adding: false, editing: false, activeItem: null });
    this.acceptChanges(dataSource, () => this.handleChange());
  };

  handleCancelClick = () => {
    const { activeItem, originalData, adding } = this.state;
    const { cancelEvent } = this.props;
    if (cancelEvent && adding) {
      // [LOGGING] cancelEvent
      pushLog2DV2LogServer.info('SEGMENTIO', cancelEvent, {}, {}, null);
    }
    if (!activeItem) return;
    activeItem.set({ ...originalData });
    activeItem.$changed = originalData && originalData.$changed;
    this.setState({ adding: false, editing: false, activeItem: null });
  };

  render () {
    const { allowAdd = false, textAddButton = 'Add', isReadOnly = false, addTitle, editTitle } = this.props;
    const {
      disabledOkButton,
      loading,
      dataSource,
      adding,
      editing
    } = this.state;

    return (
      <div className="turing-ui-v2-editable-list">
        {dataSource.map(this.renderItem)}
        {!isReadOnly && allowAdd
          ? (
            <Button
              className="turing-ui-v2-editable-list-btn-add"
              onClick={this.handleAddClick}
            >
              {textAddButton}
            </Button>
          )
          : null}
        <Modal
          visible={adding || editing}
          title={adding ? (addTitle || 'Add a new record') : (editTitle || 'Update the record')}
          onOk={this.handleOkClick}
          onCancel={this.handleCancelClick}
          okButtonProps={{ disabled: disabledOkButton }}
          confirmLoading={loading}
        >
          {this.renderForm()}
        </Modal>
      </div>
    );
  }
}

EditableList.propTypes = {
  dataSource: PropTypes.array,
  onChange: PropTypes.func,
  renderItem: PropTypes.func,
  renderForm: PropTypes.func,
  validation: PropTypes.func,
  allowAdd: PropTypes.bool,
  isReadOnly: PropTypes.any,
  textAddButton: PropTypes.string,
  addEvent: PropTypes.string,
  cancelEvent: PropTypes.string,
  saveEvent: PropTypes.string,
  addTitle: PropTypes.string,
  editTitle: PropTypes.string
};

export default EditableList;
